import { useQuery } from "@tanstack/react-query";
import { useApi } from "src/contexts/api.context";
import CostWorker from "src/workers/Cost.worker?worker";
import {
  type CostWorkerMessage_Request,
  type CostWorkerMessage_Response,
} from "src/workers/Cost.worker";
import { type ProjectCostingCalculationHttpResource } from "src/service/cost/resource/project/project-costing-calculation-resource";
import type { World } from "src/types/World";
import type { Building } from "src/types/Building";
import type { Api } from "src/types/Api";
import { ZoneType } from "src/types/Zone";

export type MetricsCalculations = ProjectCostingCalculationHttpResource;
async function runCostWorker(
  data: CostWorkerMessage_Request
): Promise<CostWorkerMessage_Response["payload"]> {
  const worker = new CostWorker();
  const message = await new Promise<CostWorkerMessage_Response["payload"]>((resolve) => {
    worker.onmessage = ({ data }: MessageEvent<CostWorkerMessage_Response>) => {
      resolve(data.payload);
    };
    worker.postMessage(data);
  });
  worker.terminate();
  return message;
}

export function useMetricsCalculationsQuery(
  projectId: string,
  world: World | null,
  selectedBuildings?: Building[]
) {
  const api = useApi();
  const allBuildingsIds = new Set(world?.buildings.map((building) => building.id));
  const selection = selectedBuildings
    ?.filter((building) => building.parent?.parent?.zoneType === ZoneType.buildable)
    ?.map((building) => building.id)
    .filter((id) => allBuildingsIds.has(id));
  const selectedBuildingsId = selection?.length
    ? selection
    : world?.buildings.map((building) => building.id);

  return useQuery({
    // we need "cost-metrics" here to be able to invalidate the query when cost settings change
    queryKey: ["cost-metrics", world?.rgWorldHashKey, selectedBuildingsId],
    staleTime: Infinity,
    enabled: !!world && !!selectedBuildingsId && !!world.rgWorldHashKey,
    queryFn: async () =>
      world && selectedBuildingsId
        ? fetchMetricCalculations(api, projectId, world, selectedBuildingsId)
        : null,
  });
}

export async function fetchMetricCalculations(
  api: Api,
  projectId: string,
  world: World,
  buildings: string[]
): Promise<ProjectCostingCalculationHttpResource | null> {
  const rgWorldZip = await world.getRgWorldZip();
  const payload = await runCostWorker({
    rgWorldZip,
    selectedBuildingsIds: buildings,
  });
  return await api.PostCostingCalculation(projectId, payload);
}
